import { useStateManagerContext } from "../../context/stateManager";
import { useGeocodes } from "../../hooks/useGeocodes";
import { useSuggestions } from "../../hooks/useSuggestions";
import { useLocalContent } from "../../sdks/LocalContent/context";
import { LocalContentProps } from "../../sdks/LocalContent/types";
import { textWithHighlighting } from "../../utils/formatters/textWithHighlighting";
import * as S from "./styles";
import { Input } from "@local-logic/core/ui";
import { primitiveUtils, browserUtils } from "@local-logic/core/utils";
import { motion } from "framer-motion";
import React, { useState, useEffect, useMemo } from "react";
import AnimateHeight from "~/components/AnimateHeight";
import { Suggestion } from "~/features/suggestions";
import useDebounce from "~/hooks/useDebounce";

export interface SearchProps {
  label?: string;
  placeholder: string;
  onSearch: (location: {
    address: string;
    latitude: number;
    longitude: number;
  }) => void;
  renderListAbove?: boolean;
  renderListAbovePosition?: number;
  onSelect?: () => void;
  onBlur?: () => void;
}

const Search = ({
  label,
  onSearch,
  onSelect,
  placeholder,
  renderListAbove = true,
  renderListAbovePosition = 0,
}: SearchProps) => {
  const { isInputAutoFocused, country } = useLocalContent();
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const [query, setQuery] = useState("");
  const [locationId, setLocationId] = useState("");
  const { data: geocodes, isLoading: geocodesLoading } =
    useGeocodes(locationId);
  const debouncedQuery = useDebounce(query, 300);
  const [showResults, setShowResults] = useState(false);
  const { data } = useSuggestions(
    debouncedQuery,
    `${sdkState.options?.lat},${sdkState.options?.lng}`,
    country
  );

  const suggestionResults = useMemo(
    () =>
      !query
        ? browserUtils.commuteHistory.getSuggestionHistory()
        : data?.suggestions || [],
    [query, data?.suggestions]
  );

  useEffect(() => {
    if (!geocodes || geocodesLoading) {
      return;
    }

    onSearch({
      address: geocodes.Location.Address.Label as unknown as string,
      longitude: geocodes.Location.DisplayPosition.Longitude,
      latitude: geocodes.Location.DisplayPosition.Latitude,
    });

    setShowResults(false);
  }, [geocodes]);

  const handleItemClick = ({
    locationId: lid,
    address,
  }: Partial<Suggestion>) => {
    if (!lid || !address) {
      return;
    }

    setLocationId(lid);
    setQuery(primitiveUtils.string.formatAddress(address));
    browserUtils.commuteHistory.push({ locationId: lid, address });
  };

  return (
    <S.Search onSelect={onSelect}>
      <Input
        autofocus={isInputAutoFocused}
        value={query}
        label={label}
        placeholder={placeholder}
        onFocus={() => setShowResults(true)}
        onBlur={() => setShowResults(false)}
        onChange={(e) => {
          setQuery(e.currentTarget.value);
          setShowResults(true);
        }}
      />
      <S.SearchList
        $renderListAbove={renderListAbove}
        $renderListAbovePosition={renderListAbovePosition}
      >
        <AnimateHeight open={showResults && !!suggestionResults.length}>
          <S.SearchListUl>
            {suggestionResults.map((suggestion, i) => (
              <S.SearchListItem key={suggestion.locationId}>
                <S.ItemButton
                  onClick={() =>
                    handleItemClick(suggestion as Partial<Suggestion>)
                  }
                >
                  <motion.div
                    animate={{ y: [10, 0], opacity: [0, 1] }}
                    transition={{
                      delay: (i + 1) * 0.03,
                      type: "spring",
                      duration: 0.4,
                    }}
                  >
                    {textWithHighlighting(
                      primitiveUtils.string.formatAddress(suggestion.address),
                      query
                    )}
                  </motion.div>
                </S.ItemButton>
              </S.SearchListItem>
            ))}
          </S.SearchListUl>
        </AnimateHeight>
      </S.SearchList>
    </S.Search>
  );
};

export default Search;
